import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Button, TextField, MenuItem, Alert, LinearProgress, Typography, Divider, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import useFetch from '../../hooks/useFetch';
import VendorPicker from './VendorPicker';
import { parseMoney, formatCents } from './money';

const duplicateCheckNumber = error => /^check number (\d+) is already on the calendar/.exec(error || '');
const emptyBalance = { owedCents: 0, notCoveredCents: 0, openingInvoiceId: null };

export default function VendorChecksFlow({ date, accounts, setAuth, onSaved, onChanged, onCancel, vendorId: presetVendorId, bankAccountId: presetBankAccountId, action: presetAction }) {
  const { t } = useTranslation();
  const { fetchResource, isPending } = useFetch({ setAuth });
  const locked = Boolean(presetVendorId && presetBankAccountId);
  const operating = accounts.filter(a => a.role === 'operating');

  const [vendorId, setVendorId] = useState(presetVendorId);
  const [bankAccountId, setBankAccountId] = useState(presetBankAccountId);
  const [step, setStep] = useState(locked ? (presetAction || 'hub') : 'pick');
  const [vendors, setVendors] = useState([]);
  const [balance, setBalance] = useState(null);
  const [amountText, setAmountText] = useState('');
  const [checks, setChecks] = useState([]);
  const [error, setError] = useState('');
  const [raisedNote, setRaisedNote] = useState(null);

  const rowValid = c => /^\d+$/.test(c.checkNumber) && parseMoney(c.amountText) > 0 && /^\d{4}-\d{2}-\d{2}$/.test(c.dueDate);
  const checkNumbers = checks.map(c => c.checkNumber).filter(Boolean);
  const duplicateNumbers = new Set(checkNumbers.filter((n, i) => checkNumbers.indexOf(n) !== i));
  const checksValid = checks.length > 0 && checks.every(rowValid) && duplicateNumbers.size === 0;
  const editRow = (i, patch) => setChecks(checks.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const addRow = () => setChecks([...checks, { checkNumber: '', amountText: '', dueDate: checks[checks.length - 1]?.dueDate || date }]);
  const removeRow = i => setChecks(checks.filter((_, j) => j !== i));

  const loadVendors = loadingKey => fetchResource({ method: 'GET', resource: 'calendar/vendors', loadingKey }).then(r => {
    if (!r.error) setVendors(r.vendors || []);
    return r;
  });

  useEffect(() => {
    if (!locked) loadVendors().then(r => { if (r.error) setError(r.error); });
  }, [fetchResource]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshBalance = async () => {
    const r = await fetchResource({ method: 'GET', resource: `calendar/vendors/${vendorId}`, loadingKey: 'balance' });
    if (r.error) { setError(r.error); return null; }
    const found = (r.balances || []).find(b => b.bankAccountId === bankAccountId) || { bankAccountId, ...emptyBalance };
    setBalance(found);
    return found;
  };

  useEffect(() => {
    if (vendorId && bankAccountId) refreshBalance();
  }, [vendorId, bankAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step === 'pick' && vendorId && bankAccountId) setStep('hub');
  }, [step, vendorId, bankAccountId]);

  useEffect(() => {
    if (step === 'checks' && checks.length === 0) setChecks([{ checkNumber: '', amountText: '', dueDate: date }]);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const goBackToPick = () => { setRaisedNote(null); setBankAccountId(undefined); setStep('pick'); };

  const submitAmount = async mode => {
    setError('');
    setRaisedNote(null);
    const amountCents = parseMoney(amountText);
    const path = mode === 'set' ? 'opening' : 'opening/add';
    const r = await fetchResource({ resource: `calendar/vendors/${vendorId}/${path}`, body: { bankAccountId, amountCents }, loadingKey: 'save' });
    if (r.error) return setError(r.error);
    setAmountText('');
    await refreshBalance();
    setStep('hub');
    onChanged?.();
  };

  const saveChecks = async () => {
    setError('');
    if (!checksValid) return;
    const body = { bankAccountId, checks: checks.map(c => ({ checkNumber: c.checkNumber, dueDate: c.dueDate, amountCents: parseMoney(c.amountText) })) };
    const r = await fetchResource({ resource: `calendar/vendors/${vendorId}/checks`, body, loadingKey: 'save' });
    if (r.error) {
      const dup = duplicateCheckNumber(r.error);
      return setError(dup ? t('calendar.checks.duplicate', { number: dup[1] }) : r.error);
    }
    setChecks([{ checkNumber: '', amountText: '', dueDate: date }]);
    await refreshBalance();
    setStep('hub');
    onChanged?.();
    setRaisedNote(r.raisedOwedCents > 0 ? r.raisedOwedCents : null);
  };

  const amountValid = mode => {
    const cents = parseMoney(amountText);
    if (cents == null) return false;
    return mode === 'add' ? cents > 0 : cents >= 0;
  };

  if (step === 'pick') return (
    <Stack spacing={2}>
      {error && <Alert severity='warning'>{error}</Alert>}
      <VendorPicker vendors={vendors} loadVendors={loadVendors} fetchResource={fetchResource} isPending={isPending}
        value={vendorId} onChange={setVendorId} />
      <TextField select label={t('calendar.checks.account')} value={bankAccountId ?? ''}
        onChange={e => setBankAccountId(e.target.value === '' ? undefined : Number(e.target.value))}>
        {operating.map(a => <MenuItem key={a.id} value={a.id}>{a.nickname}</MenuItem>)}
      </TextField>
      <Button onClick={onCancel}>{t('common.cancel')}</Button>
    </Stack>
  );

  if (step === 'hub') {
    if (!balance) return (
      <Stack spacing={2}>
        {error && <Alert severity='warning'>{error}</Alert>}
        {!error && <LinearProgress />}
        {error && <Button onClick={onCancel}>{t('common.cancel')}</Button>}
      </Stack>
    );
    return (
      <Stack spacing={2}>
        {error && <Alert severity='warning'>{error}</Alert>}
        <Typography variant='subtitle1'>{t('calendar.vendorChecks.owed')}: {formatCents(balance.owedCents)}</Typography>
        <Typography variant='body2'>{t('calendar.vendorChecks.notCovered')}: {formatCents(balance.notCoveredCents)}</Typography>
        {raisedNote != null && <Alert severity='info'>{t('calendar.vendorChecks.raisedOwed', { amount: formatCents(raisedNote) })}</Alert>}
        <Button variant='contained' onClick={() => { setRaisedNote(null); setStep('set'); }}>{t('calendar.vendorChecks.setOwed')}</Button>
        <Button variant='outlined' onClick={() => { setRaisedNote(null); setStep('add'); }}>{t('calendar.vendorChecks.addOwed')}</Button>
        <Button variant='outlined' onClick={() => { setRaisedNote(null); setStep('checks'); }}>{t('calendar.vendorChecks.addChecks')}</Button>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {!locked && <Button onClick={goBackToPick}>{t('common.back')}</Button>}
          <Button onClick={onSaved}>{t('common.done')}</Button>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
        </Stack>
      </Stack>
    );
  }

  if (step === 'set' || step === 'add') return (
    <Stack spacing={2}>
      {error && <Alert severity='warning'>{error}</Alert>}
      {balance && <Typography variant='body2'>{t('calendar.vendorChecks.owed')}: {formatCents(balance.owedCents)}</Typography>}
      <TextField autoFocus label={t('calendar.checks.amount')} value={amountText} inputProps={{ inputMode: 'decimal' }} onChange={e => setAmountText(e.target.value)} />
      <Stack direction='row' spacing={1}>
        <Button onClick={() => setStep('hub')}>{t('common.back')}</Button>
        <Button variant='contained' disabled={isPending('save') || !amountValid(step)} onClick={() => submitAmount(step)}>
          {step === 'set' ? t('calendar.vendorChecks.setOwed') : t('calendar.vendorChecks.addOwed')}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Stack spacing={2}>
      {error && <Alert severity='warning'>{error}</Alert>}
      <Stack spacing={1} divider={<Divider />}>
        {checks.map((c, i) => (
          <Stack key={i} data-testid='check-row' direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField size='small' label={t('calendar.checks.checkNumber')} value={c.checkNumber}
              error={!/^\d+$/.test(c.checkNumber) || duplicateNumbers.has(c.checkNumber)}
              helperText={duplicateNumbers.has(c.checkNumber) ? t('calendar.checks.duplicateInList') : ''}
              onChange={e => editRow(i, { checkNumber: e.target.value.replace(/\D/g, '') })} />
            <TextField size='small' label={t('calendar.checks.amount')} value={c.amountText} inputProps={{ inputMode: 'decimal' }}
              error={!(parseMoney(c.amountText) > 0)}
              onChange={e => editRow(i, { amountText: e.target.value })} />
            <TextField size='small' type='date' label={t('calendar.checks.date')} value={c.dueDate} error={!c.dueDate} InputLabelProps={{ shrink: true }}
              onChange={e => editRow(i, { dueDate: e.target.value })} />
            {checks.length > 1 && <IconButton aria-label={t('common.delete')} onClick={() => removeRow(i)}><DeleteIcon /></IconButton>}
          </Stack>
        ))}
      </Stack>
      <Button onClick={addRow}>{t('calendar.checks.addAnother')}</Button>
      <Stack direction='row' spacing={1}>
        <Button onClick={() => setStep('hub')}>{t('common.back')}</Button>
        <Button variant='contained' disabled={isPending('save') || !checksValid} onClick={saveChecks}>{t('calendar.checks.saveChecks')}</Button>
      </Stack>
    </Stack>
  );
}
